import classNames from "classnames";
import { formBuilderGenerateFormDesignStyles, IFormBuilderForm } from "../formBuilder/editor/model/formBuilderForm";
import { formBuilderGenerateRowDesignStyles, IFormBuilderRow } from "../formBuilder/editor/model/formBuilderRow";
import { formBuilderGenerateItemDesignStyles, TFormBuilderItem, TFormBuilderItemRole } from "../formBuilder/editor/model/item/formBuilderItem";
import { websiteBuilderGenerateFormDesignStyles } from "../nodes/container/websiteBuilderForm";
import { websiteBuilderSetStyleDeclaration } from "./websiteBuilderGenerateWebsite";
import { getFormBuilderInputDomType } from "../formBuilder/editor/model/item/formBuilderInput";
import { WEBSITE_BUILDER_FORM_KEY_NAME } from "./shared/form";

function generateFormKey(form: IFormBuilderForm) {
  const input = document.createElement("input");

  input.type = "hidden";
  input.name = WEBSITE_BUILDER_FORM_KEY_NAME;
  input.value = form.key;

  return input;
}

function generateForm(form: IFormBuilderForm): HTMLFormElement {
  const formEl = document.createElement("form");

  const designStyles = formBuilderGenerateFormDesignStyles();

  formEl.className = designStyles.className;
  websiteBuilderSetStyleDeclaration(formEl, designStyles.styleDeclaration);

  formEl.appendChild(generateFormKey(form));

  const submitFieldset = document.createElement("fieldset");
  submitFieldset.className = "website-form-submit-fieldset";

  const namesUsed: string[] = [];

  form.children.forEach(row => {
    submitFieldset.appendChild(generateRow(row, namesUsed));
  });

  formEl.appendChild(submitFieldset);

  return formEl;
}

function generateRow(row: IFormBuilderRow, namesUsed: string[]): HTMLDivElement {
  const rowEl = document.createElement("div");

  const designStyles = formBuilderGenerateRowDesignStyles(row);

  rowEl.className = designStyles.className;
  websiteBuilderSetStyleDeclaration(rowEl, designStyles.styleDeclaration);

  row.children.forEach(item => {
    rowEl.appendChild(generateItem(item, namesUsed));
  });

  return rowEl;
}

function generateItem(item: TFormBuilderItem, namesUsed: string[]): HTMLDivElement {
  const itemEl = document.createElement("div");

  const designStyles = formBuilderGenerateItemDesignStyles(item);

  itemEl.className = designStyles.className;
  websiteBuilderSetStyleDeclaration(itemEl, designStyles.styleDeclaration);

  itemEl.appendChild(generateItemContent(item, namesUsed));

  return itemEl;
}

function generateFieldItemLabel(label: string, required: boolean, htmlFor: string): HTMLLabelElement {
  const labelEmpty = label == null || label.length === 0;
  
  if (labelEmpty && !required) {
    return null;
  }

  const labelEl = document.createElement("label");

  labelEl.className = classNames("item-label", {
    "item-label-required": required,
  });

  labelEl.textContent = labelEmpty && required ? "Required" : label; // TODO: Translation
  labelEl.htmlFor = htmlFor;

  return labelEl;
}

function convertValueToNameFormat(value: string) {
  return value
    .trim()
    .toLowerCase()
    .split(" ")
    .filter(v => v !== "")
    .join("_");
}

function generateItemName(role: TFormBuilderItemRole, label: string, placeholder: string, namesUsed: string[]) {
  let baseName = role.replace(/-/g, "_");
  
  if (role === "custom") {
    const labelName = convertValueToNameFormat(label);
    const placeholderName = convertValueToNameFormat(placeholder);

    if (labelName.length > 0) {
      baseName += "_" + labelName;
    } else if (placeholderName.length > 0) {
      baseName += "_" + placeholderName;
    }
  }

  let name = baseName;
  let count = 0;

  while (
    namesUsed.includes(name) ||
    name === WEBSITE_BUILDER_FORM_KEY_NAME
  ) {
    count++;
    name = `${baseName}_${count + 1}`;
  }

  namesUsed.push(name);
  
  return name;
}

function generateItemOption(label: string, value: string): HTMLOptionElement {
  const optionEl = document.createElement("option");
  optionEl.textContent = label;
  optionEl.value = value;

  return optionEl;
}

function generateItemContent(item: TFormBuilderItem, namesUsed: string[]): HTMLDivElement {
  const content = document.createElement("div");

  content.className = "item-content";

  if (item.data.type === "button") {
    const button = document.createElement("button");

    button.className = classNames("item-button website-item-button", {
      "item-button-primary": item.data.role === "submit",
      "item-button-secondary": item.data.role === "reset",
    });

    button.type = item.data.role;
    button.textContent = item.data.settings.label;

    content.appendChild(button);
  } else if (item.data.type === "checkbox") {
    const field = document.createElement("div");
    const input = document.createElement("input");
    const hiddenInput = document.createElement("input");

    field.className = "item-field item-field-row";
    input.className = "item-checkbox website-item-checkbox";

    const inputId = item.key;
    const inputName = generateItemName(item.data.role, item.data.settings.label, "", namesUsed);

    hiddenInput.type = "hidden";
    hiddenInput.name = inputName;
    hiddenInput.value = "false";

    input.id = inputId;
    input.type = "checkbox";
    input.name = inputName;
    input.value = "true";

    const label = generateFieldItemLabel(item.data.settings.label, false, inputId);

    field.appendChild(hiddenInput);
    field.appendChild(input);

    if (label != null) {
      field.appendChild(label);
    }

    content.appendChild(field);
  } else if (item.data.type === "date") {
    const field = document.createElement("div");
    const input = document.createElement("input");

    field.className = "item-field";
    input.className = "item-date";

    const inputId = item.key;

    input.id = inputId;
    input.type = "date";
    input.required = item.data.settings.required;
    input.name = generateItemName(item.data.role, item.data.settings.label, "", namesUsed);

    const label = generateFieldItemLabel(item.data.settings.label, item.data.settings.required, inputId);

    if (label != null) {
      field.appendChild(label);
    }

    field.appendChild(input);
    content.appendChild(field);
  } else if (item.data.type === "input") {
    const field = document.createElement("div");
    const input = document.createElement("input");
    
    field.className = "item-field";
    input.className = "item-input";

    const inputId = item.key;
    
    input.id = inputId;
    input.type = getFormBuilderInputDomType(item.data.role, item.data.settings);

    if (input.type === "number") {
      input.min = "0";
      input.step = "any";
    }

    input.placeholder = item.data.settings.placeholder;
    input.required = item.data.settings.required;
    input.name = generateItemName(item.data.role, item.data.settings.label, item.data.settings.placeholder, namesUsed);

    const label = generateFieldItemLabel(item.data.settings.label, item.data.settings.required, inputId);

    if (label != null) {
      field.appendChild(label);
    }

    field.appendChild(input);
    content.appendChild(field);
  } else if (item.data.type === "select") {
    const field = document.createElement("div");
    const select = document.createElement("select");

    field.className = "item-field";
    select.className = "item-select website-item-select";

    const selectId = item.key;
    
    select.id = selectId;
    select.required = item.data.settings.required;
    select.name = generateItemName(item.data.role, item.data.settings.label, item.data.settings.placeholder, namesUsed);

    const label = generateFieldItemLabel(item.data.settings.label, item.data.settings.required, selectId);

    if (label != null) {
      field.appendChild(label);
    }

    if (item.data.settings.placeholder.length > 0) {
      select.appendChild(generateItemOption(item.data.settings.placeholder, ""));
    }

    item.data.settings.options.forEach(option => {
      select.appendChild(generateItemOption(option.label, option.value));
    });

    field.appendChild(select);
    content.appendChild(field);
  } else if (item.data.type === "text") {
    const text = document.createElement(item.data.role === "heading" ? "h2" : "p");

    text.className = classNames("item-text", {
      "item-text-heading": item.data.role === "heading",
      "item-text-paragraph": item.data.role === "paragraph",
    });

    text.textContent = item.data.settings.content;

    content.appendChild(text);
  } else if (item.data.type === "textarea") {
    const field = document.createElement("div");
    const textarea = document.createElement("textarea");

    field.className = "item-field";
    textarea.className = "item-textarea";

    const textareaId = item.key;

    textarea.id = textareaId;
    textarea.placeholder = item.data.settings.placeholder;
    textarea.required = item.data.settings.required;
    textarea.name = generateItemName(item.data.role, item.data.settings.label, item.data.settings.placeholder, namesUsed);;

    const label = generateFieldItemLabel(item.data.settings.label, item.data.settings.required, textareaId);

    if (label != null) {
      field.appendChild(label);
    }

    field.appendChild(textarea);
    content.appendChild(field);
  }

  return content;
}

export function websiteBuilderGenerateForm(form: IFormBuilderForm): HTMLDivElement {
  const wbFormEl = document.createElement("div");

  const designStyles = websiteBuilderGenerateFormDesignStyles();

  wbFormEl.className = designStyles.className;
  websiteBuilderSetStyleDeclaration(wbFormEl, designStyles.styleDeclaration);

  wbFormEl.appendChild(generateForm(form));

  return wbFormEl;
}